<template>
  <div class="public-device-registration">
    <!-- Main Content -->
    <VContainer
      fluid
      class="mt-10"
    >
      <VRow no-gutters>
        <VCol
          cols="12"
          md="8"
          offset-md="2"
          lg="6"
          offset-lg="3"
        >
          <div class="registration-container pa-4 pa-md-6">
            <!-- Header -->
            <div class="text-center mb-8">
              <VIcon
                icon="tabler-device-ipad"
                size="64"
                color="primary"
                class="mb-4"
              />
              <h1 class="text-h4 font-weight-bold mb-2">
                Device Registration
              </h1>
              <p class="text-body-1 text-medium-emphasis">
                Register your device to start managing care visits
              </p>
            </div>

            <!-- Loading State -->
            <div
              v-if="loading"
              class="text-center py-12"
            >
              <VProgressCircular
                indeterminate
                size="64"
                color="primary"
                class="mb-4"
              />
              <h4 class="text-h6 mb-2">
                Loading...
              </h4>
              <p class="text-body-2 text-medium-emphasis">
                {{ loadingMessage }}
              </p>
            </div>

            <!-- Registration Form -->
            <VCard
              v-else-if="!loading"
              elevation="2"
              class="mb-6"
            >
              <VCardTitle class="d-flex align-center bg-primary">
                <VIcon
                  icon="tabler-forms"
                  class="me-2"
                />
                Device Information
              </VCardTitle>

              <VCardText class="pa-6">
                <VForm
                  ref="deviceForm"
                  v-model="formValid"
                  @submit.prevent="handleSubmit"
                >
                  <VRow>
                    <!-- Location Code Dropdown -->
                    <!--
                      <VCol cols="12">
                      <VSelect
                      v-model="form.location_code"
                      :items="locationOptions"
                      item-title="label"
                      item-value="value"
                      label="Location Code"
                      placeholder="Select a location"
                      :rules="[rules.required]"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="tabler-map-pin"
                      required
                      :loading="loadingLocations"
                      :disabled="loadingLocations"
                      :error-messages="errors.location_code"
                      clearable
                      >
                      <template #item="{ props, item }">
                      <VListItem
                      v-bind="props"
                      :title="item.raw.label"
                      :subtitle="item.raw.subtitle"
                      >
                      <template #prepend>
                      <VIcon
                      icon="tabler-building"
                      class="me-2"
                      />
                      </template>
                      </VListItem>
                      </template>
                      </VSelect>
                      <p class="text-caption text-medium-emphasis mt-1">
                      Select the location where this device will be used
                      </p>
                      </VCol> 
                    -->

                    <!-- Location -->
                    <VCol cols="12">
                      <VTextField
                        v-model="form.location_code"
                        label="Location Code"
                        placeholder="Enter Location Code"
                        :rules="[rules.required]"
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="tabler-map-pin"
                        required
                        :error-messages="errors.location_code"
                      />
                      <p class="text-caption text-medium-emphasis mt-1">
                        Location where this device will be used
                      </p>
                    </VCol>

                    <!-- Device ID -->
                    <VCol cols="12">
                      <VTextField
                        v-model="form.device_id"
                        label="Device ID"
                        placeholder="Enter device identifier"
                        :rules="[rules.required, rules.deviceId]"
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="tabler-id"
                        required
                        :error-messages="errors.device_id"
                      />
                      <p class="text-caption text-medium-emphasis mt-1">
                        Unique identifier for this device (e.g., KIOSK-001, TABLET-A1)
                      </p>
                    </VCol>

                    <!-- Device Password -->
                    <VCol cols="12">
                      <VTextField
                        v-model="form.api_secret"
                        label="Device Password"
                        placeholder="Enter Device Password"
                        :rules="[rules.required]"
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="tabler-circle-key"
                        required
                        :error-messages="errors.api_secret"
                      />
                      <p class="text-caption text-medium-emphasis mt-1">
                        Use password that configured during device setup
                      </p>
                    </VCol>

                    <!-- IP Address (Auto-populated) -->
                    <VCol cols="12">
                      <VTextField
                        v-model="form.ip_address"
                        label="IP Address"
                        placeholder="Detecting IP address..."
                        :rules="[rules.required, rules.ipAddress]"
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="tabler-network"
                        required
                        readonly
                        :loading="detectingIP"
                        :error-messages="errors.ip_address"
                      >
                        <template #append-inner>
                          <VTooltip location="top">
                            <template #activator="{ props }">
                              <VBtn
                                v-bind="props"
                                icon
                                size="small"
                                variant="text"
                                color="primary"
                                @click="detectIPAddress"
                              >
                                <VIcon icon="tabler-refresh" />
                              </VBtn>
                            </template>
                            <span>Refresh IP Address</span>
                          </VTooltip>
                        </template>
                      </VTextField>
                      <p class="text-caption text-medium-emphasis mt-1">
                        Auto-detected IP address of this device
                      </p>
                    </VCol>
                  </VRow>

                  <!-- Device Status Info -->
                  <VAlert
                    v-if="form.ip_address"
                    type="info"
                    variant="tonal"
                    class="mt-4"
                  >
                    <div class="d-flex align-center">
                      <div>
                        <h6 class="text-subtitle-2 font-weight-bold mb-1">
                          Device Network Information
                        </h6>
                        <p class="text-caption mb-0">
                          IP Address: <strong>{{ form.ip_address }}</strong>
                        </p>
                        <p
                          v-if="form.location_code"
                          class="text-caption mb-0"
                        >
                          Location: <strong>{{ selectedLocationName }}</strong>
                        </p>
                      </div>
                    </div>
                  </VAlert>

                  <!-- Action Buttons -->
                  <div class="d-flex justify-space-between mt-6">
                    <VBtn
                      variant="outlined"
                      color="secondary"
                      prepend-icon="tabler-arrow-left"
                      @click="handleReset"
                    >
                      Reset Form
                    </VBtn>

                    <VBtn
                      type="submit"
                      color="primary"
                      prepend-icon="tabler-device-floppy"
                      :loading="submitting"
                      :disabled="!formValid || submitting"
                    >
                      Register Device
                    </VBtn>
                  </div>
                </VForm>
              </VCardText>
            </VCard>

            <!-- Success Message -->
            <VCard
              v-if="registrationSuccess"
              color="success"
              variant="tonal"
              class="mb-6"
            >
              <VCardText class="text-center pa-6">
                <VIcon
                  icon="tabler-circle-check"
                  size="64"
                  color="success"
                  class="mb-4"
                />
                <h3 class="text-h5 font-weight-bold mb-2">
                  Device Registered Successfully!
                </h3>
                <p class="text-body-1 mb-4">
                  Your device has been registered and a session has been created.
                </p>
                <VDivider class="my-4" />
                <div class="text-start">
                  <p class="text-body-2 mb-2">
                    <strong>Session ID:</strong> {{ sessionInfo?.session_id || 'N/A' }}
                  </p>
                  <p class="text-body-2 mb-2">
                    <strong>Device ID:</strong> {{ sessionInfo?.device_id || form.device_id }}
                  </p>
                  <p class="text-body-2 mb-2">
                    <strong>Location:</strong> {{ sessionInfo?.location || selectedLocationName }}
                  </p>
                  <p class="text-body-2 mb-0">
                    <strong>Registered At:</strong> {{ formattedRegistrationTime }}
                  </p>
                </div>

                <!--
                  <VBtn
                  color="success"
                  variant="flat"
                  class="mt-6"
                  prepend-icon="tabler-plus"
                  @click="registerAnother"
                  >
                  Register Another Device
                  </VBtn> 
                -->
              </VCardText>
            </VCard>

            <!-- Help Section -->
            <VCard
              v-if="!registrationSuccess"
              variant="outlined"
              class="mt-6"
            >
              <VCardText>
                <div class="d-flex align-start">
                  <VIcon
                    icon="tabler-help-circle"
                    color="info"
                    class="me-3 mt-1"
                  />
                  <div>
                    <h6 class="text-subtitle-1 font-weight-bold mb-2">
                      Need Help?
                    </h6>
                    <ul class="text-body-2 text-medium-emphasis">
                      <li>Ensure your device is connected to the network</li>
                      <li>Select the appropriate location for your device</li>
                      <li>Use a unique device ID for identification</li>
                      <li>The IP address is automatically detected</li>
                    </ul>
                  </div>
                </div>
              </VCardText>
            </VCard>
          </div>
        </VCol>
      </VRow>
    </VContainer>

    <!-- Error Dialog -->
    <VDialog
      v-model="showErrorDialog"
      max-width="500"
      persistent
    >
      <VCard>
        <VCardTitle class="d-flex align-center bg-error">
          <VIcon
            icon="tabler-alert-circle"
            class="me-2"
          />
          Registration Error
        </VCardTitle>
        <VCardText class="pa-6">
          <p class="text-body-1 mb-4">
            {{ errorMessage }}
          </p>
          <VAlert
            v-if="errorDetails"
            type="error"
            variant="tonal"
            class="text-caption"
          >
            {{ errorDetails }}
          </VAlert>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="error"
            variant="flat"
            @click="closeErrorDialog"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<script setup>
import { kioskAuthState } from '@/@core/auth/kioskAuthState'
import { useToast } from '@/composables/useToast'
import { useRouter } from 'vue-router'

// Composables
const router = useRouter()
const snackbar = useToast()

// Define page metadata
definePage({
  meta: {
    layout: 'blank',
    public: true,
    unauthenticatedOnly: true,
    title: 'Device Registration',
  },
})

const auth = kioskAuthState()

// Page Accessability Checking
if (auth.token) {
  router.push({
    name: 'care-visit-kiosk-panel',
  })
}

// Reactive data
const loading = ref(false)
const loadingMessage = ref('Initializing...')
const loadingLocations = ref(false)
const detectingIP = ref(false)
const submitting = ref(false)
const formValid = ref(false)
const registrationSuccess = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')
const errorDetails = ref('')

// Form references
const deviceForm = ref(null)

// Form data
const form = ref({
  location_code: null,
  device_id: '',
  ip_address: '',
})

// Errors
const errors = ref({
  location_code: [],
  device_id: [],
  ip_address: [],
})

// Location options
const locationOptions = ref([])

// Session information
const sessionInfo = ref(null)
const registrationTime = ref(null)

// Validation rules
const rules = {
  required: value => !!value || 'This field is required',
  deviceId: value => {
    if (!value) return true
    const pattern = /^[A-Za-z0-9_-]+$/
    
    return pattern.test(value) || 'Device ID can only contain letters, numbers, hyphens, and underscores'
  },
  ipAddress: value => {
    if (!value) return true
    const pattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
    
    return pattern.test(value) || 'Invalid IP address format'
  },
}

// Computed
const selectedLocationName = computed(() => {
  if (!form.value.location_code) return ''
  const location = locationOptions.value.find(loc => loc.value === form.value.location_code)
  
  return location?.label || ''
})

const formattedRegistrationTime = computed(() => {
  if (!registrationTime.value) return 'N/A'
  
  return new Date(registrationTime.value).toLocaleString()
})

// Methods
// const fetchLocations = async () => {
//   loadingLocations.value = true
//   try {
//     const response = await locationsService.getLocations({
//       per_page: 100,
//       is_active: true,
//     })

//     // Transform locations data for dropdown
//     locationOptions.value = response.data.data.map(location => ({
//       value: location.code || location.id,
//       label: location.name,
//       subtitle: `${location.building || 'N/A'} - ${location.location_type || 'Location'}`,
//     }))

//     loadingLocations.value = false
//   } catch (error) {
//     loadingLocations.value = false
//     console.error('Error fetching locations:', error)
//     snackbar.error('Failed to load locations')
//   }
// }

const detectIPAddress = async () => {
  detectingIP.value = true
  try {
    // Using a public IP detection service
    // In production, you might want to use your own backend endpoint
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()

    form.value.ip_address = data.ip

    snackbar.success('IP address detected successfully')
  } catch (error) {
    console.error('Error detecting IP address:', error)

    // Fallback: try to get local IP (note: this might not work in all browsers)
    try {
      // Alternative: use a different service
      const fallbackResponse = await fetch('https://api.my-ip.io/ip')
      const fallbackIP = await fallbackResponse.text()

      form.value.ip_address = fallbackIP.trim()
      snackbar.success('IP address detected successfully')
    } catch (fallbackError) {
      console.error('Fallback IP detection failed:', fallbackError)
      snackbar.error('Failed to auto-detect IP address. Please enter manually.')

      // Make the field editable if auto-detection fails
      errors.value.ip_address = ['Auto-detection failed. You can manually enter the IP address.']
    }
  } finally {
    detectingIP.value = false
  }
}

const handleSubmit = async () => {
  // Validate form
  const { valid } = await deviceForm.value.validate()
  if (!valid) {
    snackbar.error('Please fill in all required fields correctly')
    
    return
  }

  submitting.value = true
  errors.value = {
    location_code: [],
    device_id: [],
    ip_address: [],
  }

  // * Register Device API Call
  await $api('/care-visit/kiosk/initialize', {
    method: 'POST',
    body: JSON.stringify({
      location_code: form.value.location_code,
      serial_number: form.value.device_id,
      api_secret: form.value.api_secret,
      ip_address: form.value.ip_address,
    }),
  }).then(({ accessToken, data }) => {
    // Simulated successful registration (remove this in production)
    // await new Promise(resolve => setTimeout(resolve, 1500))

    // Store Token
    auth.setToken(accessToken)

    // Simulated response (replace with actual API response)
    // const response = {
    //   data: {
    //     session_id: `SESSION-${Date.now()}`,
    //     device_id: form.value.device_id,
    //     location_code: form.value.location_code, // selectedLocationName.value
    //     ip_address: form.value.ip_address,
    //   },
    // }

    // sessionInfo.value = response.data
    // registrationTime.value = new Date().toISOString()
    // registrationSuccess.value = true

    submitting.value = false

    snackbar.success('Kiosk registered')

    // Redirect To Panel
    router.push({
      name: 'care-visit-kiosk-panel',
    })
  }).catch(error => {
    console.error('Error in incident submitting:', error)
      
    submitting.value = false
    console.error('Registration error:', error)

    // Handle validation errors
    if (error.response?.status === 422 && error.response?.data?.errors) {
      errors.value = error.response.data.errors
      snackbar.error('Please correct the form errors and try again')
    } else {
      errorMessage.value = error.response?.data?.message || error.message || 'Device registration failed'
      errorDetails.value = error.response?.data?.details || ''
      showErrorDialog.value = true
    }
  })
}

const handleReset = () => {
  form.value = {
    location_code: null,
    device_id: '',
    ip_address: '',
  }
  errors.value = {
    location_code: [],
    device_id: [],
    ip_address: [],
  }
  deviceForm.value?.resetValidation()

  // Re-detect IP address
  detectIPAddress()
}

const registerAnother = () => {
  registrationSuccess.value = false
  sessionInfo.value = null
  registrationTime.value = null
  handleReset()
}

const closeErrorDialog = () => {
  showErrorDialog.value = false
  errorMessage.value = ''
  errorDetails.value = ''
}

// Lifecycle
onMounted(async () => {
  loading.value = true
  loadingMessage.value = 'Loading locations...'

  // Fetch locations
  // await fetchLocations()

  // Auto-detect IP address
  loadingMessage.value = 'Detecting IP address...'
  await detectIPAddress()

  loading.value = false
})
</script>

<style scoped>
.public-device-registration {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-block-size: 100vh;
}

.registration-container {
  margin-block: 0;
  margin-inline: auto;
  max-inline-size: 700px;
}

.text-medium-emphasis {
  opacity: 0.68;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .registration-container {
    padding: 16px !important;
  }
}

@media (max-width: 480px) {
  .registration-container {
    padding: 12px !important;
  }
}

/* Print styles */
@media print {
  .public-device-registration {
    background: white !important;
  }
}
</style>
